import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Loader2, ShieldAlert, ShieldX, Clock, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/api/client";

/**
 * Admin Studio — Certificate management (Phase 1e).
 * Route: `/admin/academy/certificates`. RBAC: view_academy + studio flag.
 */
export default function AdminCertificates() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ revoked: "all", expired: "all", program_id: "", user_id: "" });
  const [revoking, setRevoking] = useState(null); // cert being revoked (modal state)

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const params = {};
      if (filters.revoked !== "all") params.revoked = filters.revoked === "yes";
      if (filters.expired !== "all") params.expired = filters.expired === "yes";
      if (filters.program_id) params.program_id = filters.program_id;
      if (filters.user_id) params.user_id = filters.user_id;
      const res = await api.get("/admin/academy/certificates", { params });
      setItems(res.data.items || []);
      setStatus("ready");
    } catch (err) {
      setError(formatApiError(err));
      setStatus("error");
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-academy-certificates-list" className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <header className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">Academy Studio</p>
        <h1 className="mt-1 text-[32px] font-semibold text-v2-text" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
          Certificates
        </h1>
      </header>

      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <FilterSelect label="Status" testid="filter-revoked" value={filters.revoked}
          options={[{ v: "all", t: "All" }, { v: "no", t: "Not revoked" }, { v: "yes", t: "Revoked" }]}
          onChange={(v) => setFilters({ ...filters, revoked: v })} />
        <FilterSelect label="Expiration" testid="filter-expired" value={filters.expired}
          options={[{ v: "all", t: "All" }, { v: "no", t: "Active" }, { v: "yes", t: "Expired" }]}
          onChange={(v) => setFilters({ ...filters, expired: v })} />
        <FilterInput label="Program ID" testid="filter-program" value={filters.program_id}
          onChange={(v) => setFilters({ ...filters, program_id: v })} />
        <FilterInput label="User ID" testid="filter-user" value={filters.user_id}
          onChange={(v) => setFilters({ ...filters, user_id: v })} />
      </div>

      {status === "loading" && (<div className="text-v2-text-muted"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>)}
      {status === "error" && (<div className="text-red-400" data-testid="admin-certs-error">{error}</div>)}

      {status === "ready" && items.length === 0 && (
        <div className="border border-dashed border-v2-border rounded-lg p-10 text-center text-v2-text-muted" data-testid="admin-certs-empty">
          No certificates match these filters.
        </div>
      )}

      {status === "ready" && items.length > 0 && (
        <table className="w-full text-sm border border-v2-border rounded-lg overflow-hidden" data-testid="admin-certs-table">
          <thead className="bg-v2-surface">
            <tr className="text-left text-v2-text-muted text-xs uppercase tracking-widest">
              <th className="p-3">Number</th>
              <th className="p-3">Recipient</th>
              <th className="p-3">Program</th>
              <th className="p-3">Issued</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-v2-border" data-testid={`admin-cert-row-${c.id}`}>
                <td className="p-3 font-mono text-xs">{c.certificate_number}</td>
                <td className="p-3">{c.user_full_name_snapshot || "—"}</td>
                <td className="p-3">{c.program_title_snapshot}</td>
                <td className="p-3">{c.issued_at ? new Date(c.issued_at).toLocaleDateString() : "—"}</td>
                <td className="p-3"><StatusPill status={c.computed_status} /></td>
                <td className="p-3 text-right">
                  {!c.revoked && (
                    <button
                      data-testid={`admin-cert-revoke-${c.id}`}
                      onClick={() => setRevoking(c)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10 transition-colors"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {revoking && (
        <RevokeModal cert={revoking} onClose={() => setRevoking(null)} onDone={() => { setRevoking(null); load(); }} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    valid: { icon: ShieldCheck, cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5", label: "Valid" },
    revoked: { icon: ShieldX, cls: "text-red-400 border-red-400/40 bg-red-400/5", label: "Revoked" },
    expired: { icon: Clock, cls: "text-amber-400 border-amber-400/40 bg-amber-400/5", label: "Expired" },
  };
  const meta = map[status] || map.valid;
  const Icon = meta.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${meta.cls}`}><Icon className="w-3 h-3" /> {meta.label}</span>;
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="text-xs text-v2-text-muted flex flex-col">
      <span className="mb-1">{label}</span>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none bg-v2-surface border border-v2-border rounded-md pl-3 pr-8 py-1.5 text-sm text-v2-text"
        >
          {options.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
        </select>
        <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-v2-text-muted pointer-events-none" />
      </div>
    </label>
  );
}

function FilterInput({ label, value, onChange, testid }) {
  return (
    <label className="text-xs text-v2-text-muted flex flex-col">
      <span className="mb-1">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="optional"
        className="bg-v2-surface border border-v2-border rounded-md px-3 py-1.5 text-sm text-v2-text min-w-[220px]"
      />
    </label>
  );
}

function RevokeModal({ cert, onClose, onDone }) {
  const [publicReason, setPublicReason] = useState("");
  const [internalReason, setInternalReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    if (publicReason.trim().length < 3 || internalReason.trim().length < 3) {
      setErr("Both reasons must be at least 3 characters.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      await api.post(`/admin/academy/certificates/${cert.id}/revoke`, {
        revoked_reason_public: publicReason.trim(),
        revoked_reason_internal: internalReason.trim(),
      });
      onDone();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="revoke-modal" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-v2-surface border border-v2-border rounded-lg max-w-md w-full p-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-5 h-5 text-red-400" />
          <h2 className="text-lg font-semibold text-v2-text">Revoke certificate</h2>
        </div>
        <p className="text-xs text-v2-text-muted mb-4">
          {cert.certificate_number} · {cert.user_full_name_snapshot}
        </p>
        <label className="block mb-3">
          <span className="text-xs text-v2-text-muted">Public reason (shown on the verification page)</span>
          <textarea data-testid="revoke-reason-public" value={publicReason}
            onChange={(e) => setPublicReason(e.target.value)}
            rows={2}
            className="mt-1 w-full bg-v2-bg border border-v2-border rounded-md px-3 py-2 text-sm text-v2-text" />
        </label>
        <label className="block mb-3">
          <span className="text-xs text-v2-text-muted">Internal reason (admin-only, never public)</span>
          <textarea data-testid="revoke-reason-internal" value={internalReason}
            onChange={(e) => setInternalReason(e.target.value)}
            rows={3}
            className="mt-1 w-full bg-v2-bg border border-v2-border rounded-md px-3 py-2 text-sm text-v2-text" />
        </label>
        {err && <div className="text-xs text-red-400 mb-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="revoke-cancel" onClick={onClose} className="px-4 py-2 rounded-md border border-v2-border text-v2-text text-sm">Cancel</button>
          <button
            data-testid="revoke-confirm"
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-red-500 text-white text-sm disabled:opacity-60"
          >
            {busy ? "Revoking…" : "Revoke certificate"}
          </button>
        </div>
      </div>
    </div>
  );
}
